import React, { useEffect, useMemo } from 'react'
import moment from 'moment'
import { Avatar, SectionCard } from '@enamelai/design-system'
import { Icon as CallIcon } from '@enamelai/design-system/icons/Call'
import { Icon as FaxIcon } from '@enamelai/design-system/icons/Fax'
import { Icon as MailIcon } from '@enamelai/design-system/icons/Mail_fill'
import { Icon as LocationIcon } from '@enamelai/design-system/icons/Location'
import { Icon as ClockIcon } from '@enamelai/design-system/icons/Clock'
import { Icon as SendInAdvanceIcon } from '@enamelai/design-system/icons/Send_in_advance_2'
import { Icon as HealthConditionIcon } from '@enamelai/design-system/icons/Health_condition'
import { Icon as AllergiesIcon } from '@enamelai/design-system/icons/Allergies'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import { Icon as UserAvatarIcon } from '@enamelai/design-system/icons/User_avatar'
import { Icon as GroupAvatarIcon } from '@enamelai/design-system/icons/Add_group_avatar'
import { Icon as InboxIcon } from '@enamelai/design-system/icons/Inbox'
import { StatusTypes } from '@components/common/StatusBadge/StatusBadge.component'
import { DEMO_PROFILE_PAYMENT_METHODS_SUMMARY } from '@assets/mockdata/PatientTray/mockAccountSummary'
import { DEMO_COMMUNICATIONS_HISTORY } from '@assets/mockdata/PatientTray/mockCommunicationsHistory'
import type { FamilyMemberInfo } from '@assets/mockdata/PatientTray/mockFamilyMembers'
import { AppointmentResponse } from '@dataTypes/appointment.type'
import { Patient } from '@dataTypes/PatientInfo.types'
import { maskString } from '@utils/mask-string'
import PatientProfileDataTable, {
  type PatientProfileDataTableColumn,
} from '@pages/PatientProfile/shared/PatientProfileDataTable.component'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import ProfileCommunicationHistoryRow from '@pages/PatientProfile/shared/ProfileCommunicationHistoryRow.component'
import PatientProfilePaymentMethodsSummary from '@pages/PatientProfile/shared/PatientProfilePaymentMethodsSummary.component'
import ProfileEmptyState from '@pages/PatientProfile/shared/ProfileEmptyState.component'
import ProfileGenderIcon from '@pages/PatientProfile/shared/ProfileGenderIcon.component'
import ProfileStatusBadge from '@pages/PatientProfile/shared/ProfileStatusBadge.component'
import { buildProfilePaymentMethodsSummary } from '@pages/PatientProfile/shared/patientProfilePaymentMethods.util'
import usePatientProfilePaymentMethods from '@pages/PatientProfile/shared/usePatientProfilePaymentMethods'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { fetchCommunicationHistory } from '@store/actions'
import '../CommunicationsHistory/PatientProfileCommunicationsHistory.css'
import './PatientProfileProfileTab.css'
import UpcomingAppointments from '../AppointmentSummary/PastAndUpcoming.component'
import {
  CommunicationChannelFilter,
  ICommunicationHistory,
} from '@dataTypes/communicationHistory.type'
import { getRecentHistoryItems } from '@utils/communicationHistory.util'
import {
  formatTitleList,
  getPatientAllergyTitles,
  getPatientMedicalConditionTitles,
} from '@utils/patient-allergies-conditions.util'
import { useSelector } from 'react-redux'
import { RootState } from '@store/index'
import { Provider } from '@dataTypes/provider.types'
import { getFullName } from '@utils/fullname.util'
import {
  formatClinicianDisplayName,
  isHygienistWorkRole,
} from '@utils/provider-display.utils'
import { MOCK_PROVIDERS } from '@assets/mockdata/Provider/mockProviders.data'
import { formatPhoneNumber } from '@utils/numberFormatter.util'

const PROFILE_COMMUNICATIONS_PREVIEW_LIMIT = 6
const PROFILE_TAB_LOADING_CONTAINER = '.patient-profile-profile'

type PreferredClinicianRef = {
  provider_id?: string
  first_name?: string
  middle_name?: string
  last_name?: string
  work_role?: string
  email?: string
  work_contact?: string
  home_number?: string
  cellphone?: string
  photo_file?: Provider['photo_file']
}

function resolvePreferredClinician(
  preferred: PreferredClinicianRef | null | undefined,
  providerList: Provider[],
  useDemoData: boolean,
): Provider | null {
  if (!preferred?.provider_id) return null

  const fromStore = providerList.find(
    (provider) => provider.provider_id === preferred.provider_id,
  )
  if (fromStore) return fromStore

  if (useDemoData) {
    const fromMocks = MOCK_PROVIDERS.find(
      (provider) => provider.provider_id === preferred.provider_id,
    )
    if (fromMocks) return fromMocks
  }

  // Fall back to the embedded patient preferred object when clinic list has no match.
  return {
    ...preferred,
    provider_id: preferred.provider_id,
    first_name: preferred.first_name ?? '',
    last_name: preferred.last_name ?? '',
  } as Provider
}

type PatientProfileProfileTabProps = {
  patient: Patient
  patientId?: string
  fullName: string
  isHIPAAView: boolean
  scheduled: AppointmentResponse[]
  familyRows: FamilyMemberInfo[]
  useDemoData: boolean
  forceEmptyStates?: boolean
  onOpenAppointmentsTab: () => void
  onOpenCommunicationsTab: () => void
  onCommunicationClick?: (item: ICommunicationHistory) => void
}

function getAge(dob: string): number {
  const birthDate = new Date(dob)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const hasHadBirthdayThisYear =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate())
  if (!hasHadBirthdayThisYear) age--
  return age
}

function hasMeaningfulValue(value?: string | null): boolean {
  if (!value) return false
  const cleaned = value.trim().toLowerCase()
  if (!cleaned) return false
  const placeholders = ['none', 'n/a', 'na', 'nil', 'null', '-', 'no']
  return !placeholders.includes(cleaned)
}

function formatHeaderPhone(value?: string | null): string {
  if (!hasMeaningfulValue(value)) return 'N/A'
  return formatPhoneNumber(value as string, false) || (value as string)
}

function formatHeaderEmail(value?: string | null): string {
  return hasMeaningfulValue(value) ? (value as string) : 'None'
}

function formatHeaderAddress(patient: Patient): string {
  const line = [patient.address, patient.city, patient.state, patient.zip]
    .filter((part) => hasMeaningfulValue(part))
    .join(', ')
  return line || 'None'
}

function contactPreferenceLabels(patient: {
  contact_via_phone: boolean
  contact_via_sms: boolean
  contact_via_email: boolean
  contact_via_app: boolean
}): string {
  const bits: string[] = []
  if (patient.contact_via_phone) bits.push('Phone call')
  if (patient.contact_via_sms) bits.push('SMS')
  if (patient.contact_via_email) bits.push('Email')
  if (patient.contact_via_app) bits.push('App')
  return bits.length ? bits.join(', ') : '—'
}

function formatLastCommaFirst(
  p: { first_name: string; last_name: string },
  isHIPAAView: boolean,
): string {
  const fn = isHIPAAView ? maskString(p.first_name) : p.first_name
  const ln = isHIPAAView ? maskString(p.last_name) : p.last_name
  if (!String(fn || '').trim() && !String(ln || '').trim()) return '—'
  return `${ln}, ${fn}`.trim()
}

function ProfileViewAllButton({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}): React.ReactElement {
  return (
    <button
      type="button"
      className="ds-section-card__icon-button patient-profile-profile-title-chevron"
      aria-label={label}
      onClick={onClick}
    >
      <ArrowRightIcon aria-hidden="true" width={16} height={16} />
    </button>
  )
}

function ProfileHeaderField({
  icon,
  label,
  value,
  emphasizedValue = false,
  wrapValue = false,
}: {
  icon: React.ReactNode
  label: string
  value: string
  emphasizedValue?: boolean
  wrapValue?: boolean
}): React.ReactElement {
  return (
    <div className="patient-profile-profile-header-field">
      <p className="patient-profile-profile-header-field-label">
        {icon}
        {label}
      </p>
      <p
        className={`patient-profile-profile-header-field-value${emphasizedValue ? ' patient-profile-profile-header-field-value--emphasized' : ''}${wrapValue ? ' patient-profile-profile-header-field-value--wrap' : ''}`}
      >
        {value}
      </p>
    </div>
  )
}

function ProfileAboutItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}): React.ReactElement {
  return (
    <div className="patient-profile-profile-about-item">
      <p className="patient-profile-profile-about-item-label">
        {icon}
        {label}
      </p>
      <p className="patient-profile-profile-about-item-value">{value}</p>
    </div>
  )
}

function ProfileProviderCard({
  provider,
}: {
  provider: Provider
}): React.ReactElement {
  const displayName = formatClinicianDisplayName(provider, {
    fallback: getFullName(provider) || '—',
    preferStyle: isHygienistWorkRole(provider?.work_role)
      ? 'hygienist'
      : 'provider',
  })

  const photoPath = provider?.photo_file?.file_path

  return (
    <div className="patient-profile-profile-provider-card">
      {photoPath ? (
        <img
          className="patient-profile-profile-provider-photo"
          src={photoPath}
          alt="Provider photo"
        />
      ) : (
        <span
          className="patient-profile-profile-provider-photo patient-profile-profile-provider-photo--fallback"
          aria-hidden="true"
        >
          <i className="mgc mgc_user_3_fill" />
        </span>
      )}
      <div className="patient-profile-profile-provider-body">
        <div className="patient-profile-profile-provider-title-row">
          <p className="patient-profile-profile-provider-name">{displayName}</p>
          <span className="patient-profile-profile-provider-role">
            {provider?.work_role || '—'}
          </span>
        </div>
        <div className="patient-profile-profile-provider-contacts">
          <div className="patient-profile-profile-provider-contact">
            <MailIcon aria-hidden="true" width={16} height={16} />
            <span>{provider?.email || '—'}</span>
          </div>
          <div className="patient-profile-profile-provider-contact">
            <CallIcon aria-hidden="true" width={16} height={16} />
            <span>Work: {provider?.work_contact || '—'}</span>
          </div>
          <div className="patient-profile-profile-provider-contact">
            <CallIcon aria-hidden="true" width={16} height={16} />
            <span>Home: {provider?.home_number || '—'}</span>
          </div>
          <div className="patient-profile-profile-provider-contact">
            <CallIcon aria-hidden="true" width={16} height={16} />
            <span>Phone: {provider?.cellphone || '—'}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function PatientProfileProfileTab({
  patient,
  fullName,
  isHIPAAView,
  scheduled,
  familyRows,
  useDemoData,
  forceEmptyStates = false,
  onOpenAppointmentsTab,
  onOpenCommunicationsTab,
  onCommunicationClick,
}: PatientProfileProfileTabProps): React.ReactElement {
  const dispatch = useAppDispatch()
  const { communicationLog, loading } = useAppSelector(
    (state) => state.patientProfile,
  )

  useEffect(() => {
    if (useDemoData || !patient.patient_id) return
    dispatch(fetchCommunicationHistory(patient.patient_id))
  }, [dispatch, patient.patient_id, useDemoData])

  const providerList = useSelector((state: RootState) => state.provider.data)
  const addressLine = formatHeaderAddress(patient)

  const paymentMethodsState = usePatientProfilePaymentMethods({
    enabled: !useDemoData,
  })
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  const paymentSummary = useMemo(
    () =>
      useDemoData
        ? DEMO_PROFILE_PAYMENT_METHODS_SUMMARY
        : buildProfilePaymentMethodsSummary(
            paymentMethodsState.savedCardDisplays,
          ),
    [useDemoData, paymentMethodsState.savedCardDisplays],
  )

  useEffect(() => {
    if (!useDemoData && paymentMethodsState.isLoading) {
      showLoading({ container: PROFILE_TAB_LOADING_CONTAINER })
    } else {
      hideLoading(PROFILE_TAB_LOADING_CONTAINER)
    }
    return () => hideLoading(PROFILE_TAB_LOADING_CONTAINER)
  }, [useDemoData, paymentMethodsState.isLoading, showLoading, hideLoading])

  const communicationItems = useMemo(() => {
    const source = useDemoData ? DEMO_COMMUNICATIONS_HISTORY : communicationLog
    return getRecentHistoryItems(
      source,
      CommunicationChannelFilter.All,
      PROFILE_COMMUNICATIONS_PREVIEW_LIMIT,
    )
  }, [useDemoData, communicationLog])

  const isLoadingCommunications =
    !useDemoData && Boolean(patient.patient_id) && loading.communication

  const familyColumns =
    useMemo((): PatientProfileDataTableColumn<FamilyMemberInfo>[] => {
      const memberName = (fm: FamilyMemberInfo): string =>
        formatLastCommaFirst(fm.patient_member_info, isHIPAAView)
      const memberStatus = (fm: FamilyMemberInfo): StatusTypes =>
        fm.patient_member_id === 'mock-fam-1-2'
          ? StatusTypes.Inactive
          : StatusTypes.Active
      const dobTime = (fm: FamilyMemberInfo): number => {
        const dob = fm.patient_member_info.date_of_birth
        return dob ? moment(dob).valueOf() : 0
      }

      return [
        {
          key: '__memberName',
          label: 'Name',
          render: memberName,
          sortValue: memberName,
        },
        {
          key: 'relation_to_patient',
          label: 'Relationship',
        },
        {
          key: '__birthday',
          label: 'Birthday',
          render: (fm) => {
            const dob = fm.patient_member_info.date_of_birth
            return dob ? moment(dob).format('MM/DD/YYYY') : '—'
          },
          sortValue: dobTime,
        },
        {
          key: '__age',
          label: 'Age',
          render: (fm) => {
            const dob = fm.patient_member_info.date_of_birth
            return dob ? `${getAge(dob)} yrs old` : '—'
          },
          sortValue: (fm) => -dobTime(fm),
        },
        {
          key: '__gender',
          label: 'Gender',
          render: (fm) => fm.patient_member_info.gender || '—',
          sortValue: (fm) => fm.patient_member_info.gender || '',
        },
        {
          key: '__status',
          label: 'Status',
          render: (fm) => <ProfileStatusBadge status={memberStatus(fm)} />,
          sortValue: memberStatus,
        },
      ]
    }, [isHIPAAView])

  return (
    <div className="patient-profile-profile">
      <article className="patient-profile-profile-card patient-profile-profile-header">
        <div className="patient-profile-profile-header-inner">
          <div className="patient-profile-profile-header-identity">
            <Avatar
              src={patient?.photo_file?.file_path ?? patient.photo ?? undefined}
              name={fullName}
              alt=""
              size={64}
              className="patient-profile-profile-header-avatar"
            />
            <div>
              <p className="patient-profile-profile-header-name">{fullName}</p>
              <div className="patient-profile-profile-header-dob">
                {patient.date_of_birth ? (
                  <>
                    <span>
                      {moment(patient.date_of_birth).format('MM/DD/YYYY')} (
                      {getAge(patient.date_of_birth)} years)
                    </span>
                    <ProfileGenderIcon gender={patient.gender} />
                  </>
                ) : (
                  '—'
                )}
              </div>
            </div>
          </div>

          <div className="patient-profile-profile-header-section patient-profile-profile-header-section--contact">
            <div className="patient-profile-profile-header-fields">
              <ProfileHeaderField
                icon={
                  <CallIcon
                    className="patient-profile-icon"
                    aria-hidden="true"
                  />
                }
                label="Mobile number"
                value={
                  hasMeaningfulValue(patient.cellphone)
                    ? formatHeaderPhone(patient.cellphone)
                    : 'None'
                }
              />
              <ProfileHeaderField
                icon={
                  <CallIcon
                    className="patient-profile-icon"
                    aria-hidden="true"
                  />
                }
                label="Home number"
                value={formatHeaderPhone(patient.home_phone)}
              />
              <ProfileHeaderField
                icon={
                  <CallIcon
                    className="patient-profile-icon"
                    aria-hidden="true"
                  />
                }
                label="Work number"
                value={formatHeaderPhone(patient.work_phone)}
              />
              <ProfileHeaderField
                icon={
                  <FaxIcon
                    className="patient-profile-icon"
                    aria-hidden="true"
                  />
                }
                label="Fax number"
                value={formatHeaderPhone(patient.fax_number)}
              />
              <ProfileHeaderField
                icon={
                  <MailIcon
                    className="patient-profile-icon"
                    aria-hidden="true"
                  />
                }
                label="Email"
                value={formatHeaderEmail(patient.email)}
              />
            </div>
          </div>

          <div className="patient-profile-profile-header-section patient-profile-profile-header-section--address">
            <div className="patient-profile-profile-header-fields">
              <ProfileHeaderField
                icon={
                  <LocationIcon
                    className="patient-profile-icon"
                    aria-hidden="true"
                  />
                }
                label="Address"
                value={addressLine}
                emphasizedValue
                wrapValue
              />
            </div>
          </div>
        </div>
      </article>

      <div className="patient-profile-profile-content">
        <div className="patient-profile-profile-top-row">
          <div className="patient-profile-profile-left-col">
            <article className="patient-profile-profile-card patient-profile-profile-about-card">
              <div className="patient-profile-profile-about-bar">
                <ProfileAboutItem
                  icon={
                    <ClockIcon
                      className="patient-profile-icon patient-profile-profile-about-icon--time"
                      aria-hidden="true"
                    />
                  }
                  label="Preferred appt. time"
                  value={patient.preferred_appointment_time || '—'}
                />
                <ProfileAboutItem
                  icon={
                    <SendInAdvanceIcon
                      className="patient-profile-icon patient-profile-profile-about-icon--contact"
                      aria-hidden="true"
                    />
                  }
                  label="Contact preference"
                  value={contactPreferenceLabels(patient)}
                />
                <ProfileAboutItem
                  icon={
                    <HealthConditionIcon
                      className="patient-profile-icon patient-profile-profile-about-icon--medical"
                      aria-hidden="true"
                    />
                  }
                  label="Medical conditions"
                  value={formatTitleList(
                    getPatientMedicalConditionTitles(patient),
                  ).replace('N/A', 'None')}
                />
                <ProfileAboutItem
                  icon={
                    <AllergiesIcon
                      className="patient-profile-icon patient-profile-profile-about-icon--allergy"
                      aria-hidden="true"
                    />
                  }
                  label="Allergies"
                  value={formatTitleList(
                    getPatientAllergyTitles(patient),
                  ).replace('N/A', 'None')}
                />
              </div>
            </article>

            <SectionCard
              title="Preferred providers"
              className="patient-profile-section patient-profile-profile-providers-card"
            >
              {(() => {
                const preferredProvider = resolvePreferredClinician(
                  patient.preferredProvider,
                  providerList,
                  useDemoData,
                )
                const preferredHygienist = resolvePreferredClinician(
                  patient.preferredHygienist,
                  providerList,
                  useDemoData,
                )
                const hasPreferredProviders =
                  !forceEmptyStates &&
                  Boolean(preferredProvider || preferredHygienist)

                if (!hasPreferredProviders) {
                  return (
                    <ProfileEmptyState
                      icon={UserAvatarIcon}
                      title="No preferred providers"
                      description="You don't have any preferred providers on record."
                    />
                  )
                }

                return (
                  <div className="patient-profile-profile-providers-list">
                    {preferredProvider ? (
                      <ProfileProviderCard
                        key={preferredProvider.provider_id}
                        provider={preferredProvider}
                      />
                    ) : null}
                    {preferredHygienist ? (
                      <ProfileProviderCard
                        key={preferredHygienist.provider_id}
                        provider={preferredHygienist}
                      />
                    ) : null}
                  </div>
                )
              })()}
            </SectionCard>
          </div>

          <SectionCard
            title="Upcoming appointments"
            className="patient-profile-section patient-profile-profile-upcoming-card"
            headerAfterTitle={
              <ProfileViewAllButton
                label="Open appointments tab"
                onClick={onOpenAppointmentsTab}
              />
            }
          >
            <UpcomingAppointments
              isPast={false}
              appointmentsList={forceEmptyStates ? [] : scheduled}
              onUpdateData={() => {}}
              isNoData={forceEmptyStates || scheduled.length === 0}
              disableHeaderElements={true}
              pageSize={10}
            />
          </SectionCard>
        </div>

        <div className="patient-profile-profile-bottom-row">
          <div className="patient-profile-profile-left-col">
            <PatientProfilePaymentMethodsSummary
              summary={paymentSummary}
              forceEmpty={forceEmptyStates}
            />

            <SectionCard
              title="Family member"
              className="patient-profile-section"
            >
              {!forceEmptyStates &&
              (patient.family_members_info || []).length > 0 ? (
                <PatientProfileDataTable
                  columns={familyColumns}
                  rows={(patient.family_members_info || []).map((fm: any) => ({
                    ...fm,
                    patient_member_id: fm.patient_member_id,
                    relation_to_patient: fm.relation_to_patient,
                    status: fm.patient_member_info.status || 'Active',
                    patient_member_info: {
                      ...fm.patient_member_info,
                    },
                  }))}
                  rowKey={(fm) => fm.patient_member_id}
                  defaultSort={{ key: '__memberName', dir: 'asc' }}
                  showPagination
                  initialPageSize={10}
                  pageSizeOptions={[10, 25, 50, 100]}
                />
              ) : (
                <ProfileEmptyState
                  icon={GroupAvatarIcon}
                  title="No family members"
                  description="You don't have any family members on record."
                />
              )}
            </SectionCard>
          </div>

          <SectionCard
            title="Communications history"
            className="patient-profile-section patient-profile-profile-communications-card"
            headerAfterTitle={
              <ProfileViewAllButton
                label="Open Communications history tab"
                onClick={onOpenCommunicationsTab}
              />
            }
          >
            {isLoadingCommunications && !forceEmptyStates ? (
              <p className="patient-profile-muted">Loading...</p>
            ) : !forceEmptyStates && communicationItems.length ? (
              <div className="patient-profile-communications-list patient-profile-profile-communications-list">
                {communicationItems.map((item) => (
                  <ProfileCommunicationHistoryRow
                    key={item.id}
                    item={item}
                    onClick={onCommunicationClick}
                  />
                ))}
              </div>
            ) : (
              <ProfileEmptyState
                icon={InboxIcon}
                title="No communications yet"
                description="You don't have any communications history on record."
              />
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  )
}

export default PatientProfileProfileTab
